import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { ExternalLink, RefreshCw, X, Check, Ban } from 'lucide-react'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import { corePaymentsApi, type CorePayment } from '../../lib/api'
import DataTable, { Column } from '../../components/shared/DataTable'
import Pagination from '../../components/shared/Pagination'
import ErrorState from '../../components/shared/ErrorState'
import { formatDatetime, formatMoney } from '../../lib/utils'
import { tx } from '../../lib/i18n'

const STATUS_STYLE: Record<string, string> = {
  verified: 'bg-indigo-100 text-indigo-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-700',
}
const STATUS_LABEL: Record<string, string> = { verified: 'ตรวจสลิปแล้ว', pending: 'รอตรวจ', failed: 'ไม่ผ่าน' }

/**
 * Money shops actually paid for plans / add-ons — read live from queza-core (public."SubscriptionPayment"),
 * where the slip is checked by Slip2Go. A slip Slip2Go couldn't verify (รอตรวจ) or rejected (ไม่ผ่าน)
 * can be approved by hand once the money is confirmed (activates the plan / add-on), or a pending one rejected.
 */
const ShopPaymentsPage: React.FC = () => {
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('')
  const [kind, setKind] = useState('')
  const [slip, setSlip] = useState<CorePayment | null>(null)
  const [params, setParams] = useSearchParams()
  const shopId = params.get('shopId') || ''
  const [decision, setDecision] = useState<{ p: CorePayment; action: 'approve' | 'reject' | 'refund' } | null>(null)
  const [reason, setReason] = useState('')
  const { showToast } = useToast()
  const { can } = usePermission()
  const qc = useQueryClient()
  const canApprove = can('shop-payments.approve')

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['core-payments', { page, status, kind, shopId }],
    queryFn: () =>
      corePaymentsApi
        .list({ page, limit: 20, status: status || undefined, kind: kind || undefined, shopId: shopId || undefined })
        .then((r) => r.data),
  })

  const decide = useMutation({
    mutationFn: () =>
      decision!.action === 'approve' ? corePaymentsApi.approve(decision!.p.id, reason.trim()) : decision!.action === 'refund' ? corePaymentsApi.refund(decision!.p.id, reason.trim()) : corePaymentsApi.reject(decision!.p.id, reason.trim()),
    onSuccess: () => {
      showToast({
        type: 'success',
        message: decision!.action === 'approve' ? 'อนุมัติแล้ว — เปิดใช้แพ็กเกจให้ร้านแล้ว' : decision!.action === 'refund' ? 'บันทึกการคืนเงินแล้ว' : 'ไม่อนุมัติแล้ว',
      })
      setDecision(null)
      setReason('')
      qc.invalidateQueries({ queryKey: ['core-payments'] })
    },
    onError: (e: unknown) =>
      showToast({
        type: 'error',
        message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'ทำรายการไม่สำเร็จ',
      }),
  })

  const rows = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1
  const pageSum = rows.filter((r) => r.status === 'verified').reduce((s, r) => s + r.amountBaht, 0)

  const columns: Column<CorePayment>[] = [
    { key: 'createdAt', header: 'วันที่', render: (r) => <span className="text-sm text-gray-500">{formatDatetime(r.verifiedAt ?? r.createdAt)}</span> },
    {
      key: 'shopName',
      header: 'ร้าน',
      render: (r) => (
        <Link to={`/shops/${r.shopId}`} className="font-medium text-gray-900 hover:text-indigo-600">
          {r.shopName || r.shopId.slice(0, 8)}
        </Link>
      ),
    },
    {
      key: 'kind',
      header: 'รายการ',
      render: (r) =>
        r.kind === 'addon' ? (
          <span className="text-sm">Add-on · {r.addonCode}</span>
        ) : (
          <span className="text-sm capitalize">
            {r.planCode} · {r.billingCycle === 'yearly' ? 'รายปี' : 'รายเดือน'}
          </span>
        ),
    },
    { key: 'amountBaht', header: 'ยอด', render: (r) => <span className="font-semibold">{formatMoney(r.amountBaht)}</span> },
    {
      key: 'status',
      header: 'สถานะ',
      render: (r) => (
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status] ?? 'bg-gray-100 text-gray-600'}`}>
          {STATUS_LABEL[r.status] ?? r.status}
        </span>
      ),
    },
    {
      key: 'period',
      header: 'รอบบริการ',
      render: (r) => (
        <span className="text-xs text-gray-500">
          {r.periodStart ? `${formatDatetime(r.periodStart).split(',')[0]} – ${r.periodEnd ? formatDatetime(r.periodEnd).split(',')[0] : ''}` : '-'}
        </span>
      ),
    },
    {
      key: 'slip',
      header: 'สลิป',
      render: (r) =>
        r.slipImageUrl ? (
          <button onClick={() => setSlip(r)} className="text-xs text-indigo-600 hover:underline">
            ดูสลิป
          </button>
        ) : (
          <span className="text-xs text-gray-300">-</span>
        ),
    },
    ...(canApprove
      ? [
          {
            key: 'actions',
            header: 'ตรวจเอง',
            render: (r: CorePayment) =>
              r.status === 'verified' ? (
                r.refundedAt ? (
                  <span className="text-xs font-medium text-red-600" data-testid="payment-refunded" title={r.refundReason ?? ''}>คืนเงินแล้ว</span>
                ) : (
                  <button
                    onClick={() => setDecision({ p: r, action: 'refund' })}
                    className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                    data-testid="payment-refund"
                  >
                    คืนเงิน
                  </button>
                )
              ) : (
                <div className="flex gap-1">
                  <button
                    onClick={() => setDecision({ p: r, action: 'approve' })}
                    className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg border border-green-300 text-green-700 hover:bg-green-50"
                    data-testid="payment-approve"
                  >
                    <Check className="h-3.5 w-3.5" /> อนุมัติ
                  </button>
                  {r.status === 'pending' && (
                    <button
                      onClick={() => setDecision({ p: r, action: 'reject' })}
                      className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                      data-testid="payment-reject"
                    >
                      <Ban className="h-3.5 w-3.5" /> ไม่อนุมัติ
                    </button>
                  )}
                </div>
              ),
          } as Column<CorePayment>,
        ]
      : []),
  ]

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{tx('Shop Payments')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            เงินที่ร้านจ่ายค่าแพ็กเกจ / add-on จริง (ข้อมูลสดจาก queza-core, ตรวจสลิปด้วย Slip2Go)
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> {tx('Refresh')}
        </button>
      </div>

      {shopId && (
        <div className="flex items-center gap-2 text-sm bg-indigo-50 border border-indigo-100 text-indigo-800 rounded-lg px-3 py-2" data-testid="payments-shop-filter">
          เฉพาะร้าน <span className="font-mono">{shopId.slice(0, 8)}</span>
          <button
            onClick={() => {
              params.delete('shopId')
              setParams(params)
              setPage(1)
            }}
            className="ml-auto text-xs underline"
          >
            ดูทุกร้าน
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {[
          ['', 'ทุกสถานะ'],
          ['verified', 'ตรวจแล้ว'],
          ['pending', 'รอตรวจ'],
          ['failed', 'ไม่ผ่าน'],
        ].map(([v, l]) => (
          <button
            key={v}
            onClick={() => {
              setStatus(v)
              setPage(1)
            }}
            className={`px-3 py-1.5 text-sm rounded-full border ${status === v ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          >
            {l}
          </button>
        ))}
        <select
          value={kind}
          onChange={(e) => {
            setKind(e.target.value)
            setPage(1)
          }}
          className="ml-auto border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
        >
          <option value="">แพ็กเกจ + add-on</option>
          <option value="plan">แพ็กเกจ</option>
          <option value="addon">Add-on</option>
        </select>
      </div>

      {error ? (
        <ErrorState message="โหลดข้อมูลการชำระเงินไม่ได้" onRetry={() => refetch()} />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <DataTable columns={columns} data={rows} isLoading={isLoading} emptyMessage="ยังไม่มีการชำระเงินจากร้าน" />
          {total > 0 && (
            <div className="px-4 py-3 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
              <span className="text-sm text-gray-500">
                ยอดที่ตรวจแล้วในหน้านี้: <b className="text-gray-800">{formatMoney(pageSum)}</b>
              </span>
              <Pagination page={page} totalPages={totalPages} total={total} limit={20} onPageChange={setPage} />
            </div>
          )}
        </div>
      )}

      {decision && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="payment-decision">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-base font-semibold text-gray-900">
              {decision.action === 'approve' ? 'อนุมัติการชำระเงินนี้?' : decision.action === 'refund' ? 'บันทึกว่าคืนเงินรายการนี้แล้ว?' : 'ไม่อนุมัติการชำระเงินนี้?'}
            </h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <b>{decision.p.shopName || decision.p.shopId.slice(0, 8)}</b> ·{' '}
                {decision.p.kind === 'addon' ? `Add-on ${decision.p.addonCode}` : `${decision.p.planCode} · ${decision.p.billingCycle === 'yearly' ? 'รายปี' : 'รายเดือน'}`} ·{' '}
                {formatMoney(decision.p.amountBaht)}
              </p>
              {decision.action === 'approve' ? (
                <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  ยืนยันว่าเงินเข้าบัญชีแล้ว — ระบบจะเปิดใช้{decision.p.kind === 'addon' ? ' add-on ' : 'แพ็กเกจ '}ให้ร้านทันที ({decision.p.billingCycle === 'yearly' ? '365' : '30'} วัน)
                </p>
              ) : decision.action === 'refund' ? (
                <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  ใช้หลังโอนเงินคืนให้ร้านแล้ว — ระบบบันทึกการคืนเงินและยกเลิกค่าคอมมิชชันของผู้แนะนำสำหรับรายการนี้ แพ็กเกจของร้านไม่เปลี่ยน (ยกเลิก/เปลี่ยนแพ็กเกจแยกต่างหาก) และย้อนกลับไม่ได้
                </p>
              ) : (
                <p>สถานะจะเปลี่ยนเป็น "ไม่ผ่าน" แพ็กเกจของร้านไม่เปลี่ยน</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">เหตุผล (บันทึกในประวัติ) *</label>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="เช่น ตรวจยอดในบัญชีแล้ว"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                data-testid="payment-decision-reason"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setDecision(null)
                  setReason('')
                }}
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                data-testid="payment-decision-cancel"
              >
                ยกเลิก
              </button>
              <button
                onClick={() => decide.mutate()}
                disabled={!reason.trim() || decide.isPending}
                className={`px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 ${decision.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                data-testid="payment-decision-confirm"
              >
                {decide.isPending ? 'กำลังบันทึก...' : decision.action === 'approve' ? 'อนุมัติ' : decision.action === 'refund' ? 'บันทึกการคืนเงิน' : 'ไม่อนุมัติ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {slip && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setSlip(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-semibold text-gray-900">{slip.shopName}</p>
                <p className="text-xs text-gray-500">
                  {formatMoney(slip.amountBaht)} · ref {slip.slipVerificationRef ?? '-'}
                </p>
              </div>
              <button onClick={() => setSlip(null)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <img src={slip.slipImageUrl!} alt="slip" className="w-full max-h-[70vh] object-contain rounded-lg bg-gray-50" />
            <a
              href={slip.slipImageUrl!}
              target="_blank"
              rel="noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline"
            >
              เปิดรูปเต็ม <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      )}
    </div>
  )
}

export default ShopPaymentsPage
